// SpendDashboard.jsx
import React, {useEffect, useState, useRef} from 'react'
import {
    Box,
    Button,
    Card,
    Flex,
    FormControl,
    FormLabel,
    GridItem,
    Input,
    Select,
    SimpleGrid,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    Tfoot,
    useColorModeValue,
    useBreakpointValue,
} from '@chakra-ui/react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import axiosInstance from '../../../api/axiosInstance'
import {useNotification} from '../../../contexts/NotificationContext'
import {formatNumber} from '../../../Utils/Utils'
import './custom_style.css'

dayjs.extend(localeData)

const toNumber = (value) => {
    const normalizedValue = typeof value === 'string'
        ? value.trim().replace(',', '.')
        : value
    const number = Number.parseFloat(normalizedValue)

    return Number.isFinite(number) ? number : 0
}

const SpendDashboard = () => {
    const today = dayjs().toDate()
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(true)
    const [startDate, setStartDate] = useState(today)
    const [endDate, setEndDate] = useState(today)
    const [selectedMonth, setSelectedMonth] = useState('')
    const [pagination, setPagination] = useState({current_page: 1, total_pages: 1})
    const {addNotification, addErrorNotifications} = useNotification()
    const firstLoad = useRef(false)
    const cardBg = useColorModeValue('white', 'navy.800')
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300')
    const colGap = useBreakpointValue({base: 4, md: 6})

    useEffect(() => {
        if (!firstLoad.current) {
            const s = dayjs(today).format('YYYY-MM-DD')
            fetchData(s, s)
            firstLoad.current = true
            return
        }
        if (!startDate || !endDate || startDate > endDate) return

        if (dayjs(endDate).diff(startDate, 'day') > 31) {
            addNotification('Please select a date range of up to 31 days', 'warning')
        } else {
            fetchData(dayjs(startDate).format('YYYY-MM-DD'), dayjs(endDate).format('YYYY-MM-DD'))
        }
    }, [startDate, endDate])

    const fetchData = async (start, end, page = 1) => {
        setLoading(true)
        try {
            const url = `/roas-products?start_date=${start}&end_date=${end}&page=${page}`
            const {data: res} = await axiosInstance.get(url)
            setData(res.data)
            setPagination(res.pagination)
        } catch (err) {
            addErrorNotifications(err.response?.data || err)
        } finally {
            setLoading(false)
        }
    }

    const handleSpendChange = (val, pIdx, dIdx) => {
        setData((draft) => {
            const n = [...draft]
            const spend = toNumber(val)
            const sales = toNumber(n[pIdx].daily_data[dIdx].sales)
            n[pIdx].daily_data[dIdx].spend = val === '' ? '' : spend
            n[pIdx].daily_data[dIdx].roas = spend ? (sales / spend).toFixed(2) : '#DIV/0!'
            return n
        })
    }

    const saveSingle = async (pIdx, dIdx) => {
        const {product_id} = data[pIdx]
        const item = data[pIdx].daily_data[dIdx]
        try {
            await axiosInstance.post(`/spends/${product_id}`, {
                date: item.date,
                spend: toNumber(item.spend),
            })
            addNotification('Spend saved', 'success')
        } catch (e) {
            addErrorNotifications(e.response?.data)
        }
    }

    const saveAll = async () => {
        const payload = data.map((p) => ({
            product_id: p.product_id,
            daily_data: p.daily_data.map((i) => ({
                date: i.date,
                spend: toNumber(i.spend),
            })),
        }))
        try {
            await axiosInstance.post('/spends/aggregate', {products: payload})
            addNotification('All spends saved', 'success')
        } catch (e) {
            addErrorNotifications(e.response?.data)
        }
    }

    const toPage = (pg) => {
        if (pg >= 1 && pg <= pagination.total_pages) {
            fetchData(dayjs(startDate).format('YYYY-MM-DD'), dayjs(endDate).format('YYYY-MM-DD'), pg)
        }
    }

    const monthSelect = (m) => {
        setSelectedMonth(m)
        if (m === '') return
        const idx = parseInt(m, 10)
        const start = dayjs().month(idx).startOf('month')
        const end = idx === dayjs().month() ? dayjs() : dayjs().month(idx).endOf('month')
        setStartDate(start.toDate())
        setEndDate(end.toDate())
    }

    const avgRoas = (product) => {
        const valid = product.daily_data.filter((i) => i.roas && i.roas !== '#DIV/0!')
        return valid.length ? valid.reduce((s, i) => s + Number(i.roas), 0) / valid.length : 0
    }

    if (loading) {
        return (
            <Flex h="80vh" w="full" align="center" justify="center">
                <Spinner size="xl"/>
            </Flex>
        )
    }

    return (
        <Box pt={{base: 24, md: 20}} px={{base: 4, md: 10}}>
            <Card mb={6} p={6}>
                <SimpleGrid columns={{base: 1, md: 2, lg: 4}} gap={colGap}>
                    <GridItem>
                        <FormControl>
                            <FormLabel>Start Date</FormLabel>
                            <DatePicker
                                selected={startDate}
                                onChange={setStartDate}
                                dateFormat="dd‑MM‑yyyy"
                                customInput={<Input/>}
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl>
                            <FormLabel>End Date</FormLabel>
                            <DatePicker
                                selected={endDate}
                                onChange={setEndDate}
                                dateFormat="dd‑MM‑yyyy"
                                customInput={<Input/>}
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl>
                            <FormLabel>Month (shortcut)</FormLabel>
                            <Select value={selectedMonth} onChange={(e) => monthSelect(e.target.value)}>
                                <option value="">–</option>
                                {dayjs.months().map((m, i) => (
                                    <option key={i} value={i}>{m}</option>
                                ))}
                            </Select>
                        </FormControl>
                    </GridItem>
                    <GridItem display="flex" alignItems="flex-end">
                        <Button colorScheme="blue" w="full" onClick={saveAll}>
                            Save All
                        </Button>
                    </GridItem>
                </SimpleGrid>
            </Card>

            <SimpleGrid columns={{base: 1, md: 2}} spacing={6}>
                {data.map((product, pIdx) => (
                    <Card key={product.product_id} p={4} bg={cardBg}>
                        <Text fontWeight="bold" mb={2}>
                            {product.product_name}
                        </Text>

                        <Box overflow="auto" maxH="50vh">
                            <Table size="sm" variant="simple">
                                <Thead position="sticky" top="0" zIndex="1" bg={cardBg}>
                                    <Tr>
                                        {['Day', 'Spend', 'Sales', 'ROAS', 'Orders', ''].map((h) => (
                                            <Th key={h} borderColor={borderColor}>{h}</Th>
                                        ))}
                                    </Tr>
                                </Thead>

                                <Tbody>
                                    {product.daily_data.map((d, dIdx) => (
                                        <Tr key={d.date}>
                                            <Td>{d.date}</Td>
                                            <Td>
                                                <Input
                                                    size="xs"
                                                    type="number"
                                                    min="0"
                                                    value={d.spend || ''}
                                                    onChange={(e) => handleSpendChange(e.target.value, pIdx, dIdx)}
                                                />
                                            </Td>
                                            <Td>{d.sales}</Td>
                                            <Td>{d.roas}</Td>
                                            <Td>{d.orders}</Td>
                                            <Td>
                                                <Button
                                                    size="xs"
                                                    onClick={() => saveSingle(pIdx, dIdx)}
                                                    colorScheme="teal"
                                                >
                                                    Save
                                                </Button>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>

                                <Tfoot position="sticky" bottom="0" zIndex="1" bg={cardBg}>
                                    <Tr>
                                        <Th borderColor={borderColor}>Total</Th>
                                        <Th borderColor={borderColor}>
                                            {formatNumber(product.daily_data.reduce((s, i) => s + toNumber(i.spend), 0))} RON
                                        </Th>
                                        <Th borderColor={borderColor}>
                                            {formatNumber(product.daily_data.reduce((s, i) => s + toNumber(i.sales), 0))} RON
                                        </Th>
                                        <Th borderColor={borderColor}>
                                            {formatNumber(avgRoas(product))}
                                        </Th>
                                        <Th borderColor={borderColor}>
                                            {formatNumber(product.daily_data.reduce((s, i) => s + toNumber(i.orders), 0))}
                                        </Th>
                                        <Th borderColor={borderColor}></Th>
                                    </Tr>
                                </Tfoot>
                            </Table>
                        </Box>
                    </Card>
                ))}
            </SimpleGrid>

            <Flex
                mt={8}
                justify="center"
                align="center"
                gap={4}
                position="sticky"
                bottom="0"
                bg={cardBg}
                py={3}
                boxShadow="sm"
                zIndex="0"
            >
                <Button
                    size="sm"
                    onClick={() => toPage(pagination.current_page - 1)}
                    isDisabled={pagination.current_page === 1}
                >
                    Previous
                </Button>
                <Text fontSize="sm">
                    Page {pagination.current_page} / {pagination.total_pages}
                </Text>
                <Button
                    size="sm"
                    onClick={() => toPage(pagination.current_page + 1)}
                    isDisabled={pagination.current_page === pagination.total_pages}
                >
                    Next
                </Button>
            </Flex>
        </Box>
    )
}

export default SpendDashboard
